import { useState, useEffect, useCallback } from "react";
import axios from "../customAxios";

/**
 * @typedef {object} ShippingZonePagination
 * @property {number} page
 * @property {number} rowsPerPage
 */

/**
 * @typedef {object} ShippingZoneFilter
 * @property {string} search
 */

/**
 * @typedef {object} ShippingZoneSorting
 * @property {string} orderBy
 * @property {string} order
 */

/**
 * @typedef {object} ShippingZoneData
 * @property {number} id
 * @property {string} name
 * @property {number | null} admin
 * @property {string} admin_email
 * @property {string} courier
 * @property {string} price
 * @property {string} country
 * @property {int} communes
 * @property {Boolean} isMassiveUpload
 * @property {File} file
 */

/**
 * Initializes state from localStorage.
 * @returns {{filters: ShippingZoneFilter, pagination: ShippingZonePagination, sorting: ShippingZoneSorting}}
 */
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("shippingZoneFilter");
  const defaults = {
    filters: {
      search: "",
    },
    pagination: {
      page: 1,
      rowsPerPage: 10,
    },
    sorting: {
      orderBy: "name",
      order: "asc",
    },
  };

  if (savedStateJSON) {
    const savedState = JSON.parse(savedStateJSON);
    try {
      const finalState = {
        filters: { ...defaults.filters, ...(savedState.filters || {}) },
        pagination: {
          ...defaults.pagination,
          ...(savedState.pagination || {}),
        },
        sorting: { ...defaults.sorting, ...(savedState.sorting || {}) },
      };
      return finalState;
    } catch (error) {
      console.error(
        `Error obteniendo los datos del localStorage "shippingZoneFilter": ${error}`,
      );
      return defaults;
    }
  }
  return defaults;
};

/**
 * Custom Hook to manage DATA state for the Shipping Zones page
 *
 * Handles data fetching (GET), pagination, sorting, filtering,
 * and CRUD operations (POST, PATCH, DELETE)
 * It does NOT manage UI state
 *
 * @returns {{
 * shippingZones: Array<ShippingZoneData>,
 * loading: boolean,
 * error: string | null,
 * pagination: object,
 * filters: object,
 * sorting: object,
 * shippingZonesCount: number,
 * handleChangePage: (newPage: number) => void,
 * handleChangeRowsPerPage: (newRows: number) => void,
 * handleSortChange: (orderBy: string) => void,
 * createZone: (zoneData: object) => Promise<void>,
 * updateZone: (zoneId: number, zoneData: object) => Promise<void>,
 * deleteZone: (zoneId: number) => Promise<void>,
 * massiveUpload: (zoneData: object) => Promise<void>,
 * refetchShippingZones: () => Promise<void>
 * }}
 */
export function useShippingZones() {
  // Data status and IU
  const [initialState] = useState(() => getInitialState());
  const [filters, setFilters] = useState(initialState.filters);
  const [sorting, setSorting] = useState(initialState.sorting);
  const [pagination, setPagination] = useState(initialState.pagination);
  const [shippingZones, setShippingZones] = useState([]);
  const [shippingZonesCount, setShippingZonesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // STATE MANNERS (HANDLERS)
  const handleChangePage = useCallback((newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
    setPagination({ page: 1, rowsPerPage: newRowsPerPage });
  }, []);

  const handleSortChange = useCallback((orderBy) => {
    setSorting((previous) => ({
      orderBy: orderBy,
      order:
        previous.orderBy === orderBy && previous.order === "asc"
          ? "desc"
          : "asc",
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const fetchShippingZones = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      page: pagination.page,
      page_size: pagination.rowsPerPage,
    };

    try {
      const {
        data: { results: shippingZones, count },
      } = await axios.get("shipping_zones/list/", { params });
      setShippingZones(shippingZones);
      setShippingZonesCount(count);
    } catch (error) {
      console.error(`No se pudo obtener las zonas de despacho: ${error}`);
      setError("No se pudo obtener las zonas de despacho");
    } finally {
      setLoading(false);
    }
  }, [pagination, filters, sorting]);

  const createZone = useCallback(
    async (zone) => {
      setLoading(true);
      setError(null);
      try {
        if (zone.isMassiveUpload) {
          await massiveUpload(zone);
        } else {
          await axios.post("shipping_zones/create/", zone);
        }
        fetchShippingZones();
      } catch (error) {
        console.error(`Error al crear una nueva zona de despacho: ${error}`);
        setError("Error al crear una nueva zona de despacho");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchShippingZones],
  );

  const updateZone = useCallback(
    async (zoneId, zoneData) => {
      setLoading(true);
      setError(null);
      try {
        if (zoneData.isMassiveUpload) {
          await massiveUpdate(zoneId, zoneData);
        } else {
          await axios.patch(`shipping_zones/${zoneId}/update/`, zoneData);
        }
        fetchShippingZones();
      } catch (error) {
        console.error(`Error al actualizar zona de despacho: ${error}`);
        setError("Error al actualizar zona de despacho");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchShippingZones],
  );

  const deleteZone = useCallback(
    async (zoneId) => {
      setLoading(true);
      setError(null);
      try {
        await axios.delete(`shipping_zones/${zoneId}/delete/`);
        fetchShippingZones();
      } catch (error) {
        console.error(
          `Error al intentar eliminar la zona de despacho: ${error}`,
        );
        setError("Error al intentar eliminar la zona de despacho");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchShippingZones],
  );

  const massiveUpload = useCallback(
    async (zoneData) => {
        const formData = new FormData();
        formData.append("name", zoneData.name);
        formData.append("courier", zoneData.courier);
        formData.append("price", zoneData.price);
        formData.append("admin_id", zoneData.admin);

        if (zoneData.tms) {
          formData.append("tms", zoneData.tms);
        }

        formData.append("file", zoneData.file);
        return axios.post("shipping_zones/create_massive/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
    },
    [],
  );

  const massiveUpdate = useCallback(
    async (zoneId, zoneData) => {
      const formData = new FormData();
      formData.append("name", zoneData.name);
      formData.append("courier", zoneData.courier);
      formData.append("price", zoneData.price);
      formData.append("admin_id", zoneData.admin);

      if (zoneData.tms) {
        formData.append("tms", zoneData.tms);
      }

      if (zoneData.file) {
        formData.append("file", zoneData.file);
      }

      return axios.patch(`shipping_zones/${zoneId}/update_massive/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    []
  );

  // SECONDARY EFFECTS (FETCH, DEBUNCE, LOCALSTORAGE)
  useEffect(() => {
    localStorage.setItem(
      "shippingZoneFilter",
      JSON.stringify({
        filters,
        pagination,
        sorting,
      }),
    );
  }, [filters, pagination, sorting]);

  useEffect(() => {
    fetchShippingZones();
  }, [fetchShippingZones]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    shippingZones,
    loading,
    error,
    pagination,
    filters,
    sorting,
    shippingZonesCount,

    // MANNERS (Setters y Actions)
    handleChangePage,
    handleChangeRowsPerPage,
    handleSortChange,
    createZone,
    updateZone,
    deleteZone,
    refetchShippingZones: fetchShippingZones,
  };
}
